import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Bike, Phone } from "lucide-react";
import landingConfig from "../landingConfig";
import { FacebookIcon, InstagramIcon, YoutubeIcon, WhatsappIcon, GoogleIcon } from "../components/SocialIcons";
import { getStorefrontBrand } from "../../web-order/webOrderApi";

const hasMapCoords = !!(landingConfig.mapLat && landingConfig.mapLng);
const mapsUrl = hasMapCoords
  ? `https://www.google.com/maps/search/?api=1&query=${landingConfig.mapLat},${landingConfig.mapLng}`
  : landingConfig.address
  ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(landingConfig.address)}`
  : null;

// Root ("/") landing page — a static, env-configured "digital business
// card" in front of the actual ordering flow at /order. Every section
// (logo/heading/description/order-timing/reviews/social) is independently
// optional: unset in this deployment's .env means that section just
// doesn't render (see landingConfig.js) rather than showing an empty/
// broken placeholder. Two colors drive the whole page — themeColor
// (background) and accentColor (heading/button/icon color) — so each store
// can look distinct without touching any component code.
export default function LandingPage() {
  // orderTiming/deliveryTiming ({ isOpen, todayLabel }, same shape /order's
  // own header uses), sellerWhatsApp (for the WhatsApp icon link below), and
  // businessPhone (for the click-to-call button) — all come off the same
  // public brand fetch, this page's only API call. Fetch failure just
  // leaves everything unset — this page degrades the same way every other
  // optional section here does, not with an error state.
  const [orderTiming, setOrderTiming] = useState(null);
  const [deliveryTiming, setDeliveryTiming] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState(null);
  const [businessPhone, setBusinessPhone] = useState(null);

  useEffect(() => {
    getStorefrontBrand()
      .then((brand) => {
        setOrderTiming(brand?.orderTiming || null);
        setDeliveryTiming(brand?.deliveryTiming || null);
        setWhatsappNumber(brand?.sellerWhatsApp || null);
        setBusinessPhone(brand?.businessPhone || null);
      })
      .catch(() => {});
  }, []);

  const SOCIAL_LINKS = [
    { key: "instagram", url: landingConfig.social.instagram, Icon: InstagramIcon, label: "Instagram" },
    { key: "facebook", url: landingConfig.social.facebook, Icon: FacebookIcon, label: "Facebook" },
    { key: "youtube", url: landingConfig.social.youtube, Icon: YoutubeIcon, label: "YouTube" },
    { key: "location", url: mapsUrl, Icon: MapPin, label: "Location" },
    { key: "whatsapp", url: whatsappNumber ? `https://wa.me/${whatsappNumber}` : null, Icon: WhatsappIcon, label: "WhatsApp" },
  ].filter((s) => s.url);

  return (
    <div
      className="min-h-screen flex flex-col items-center px-6 py-12 sm:py-16"
      style={{ backgroundColor: landingConfig.themeColor }}
    >
      <div className="w-full max-w-md space-y-7 text-center">
        {landingConfig.logoUrl && (
          <div className="flex justify-center">
            <img src={landingConfig.logoUrl} alt="Store logo" className="h-32 sm:h-36 w-auto object-contain" />
          </div>
        )}

        {landingConfig.storeName && (
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: landingConfig.accentColor }}>
            Welcome to {landingConfig.storeName}
          </h1>
        )}

        {landingConfig.description && (
          <p className="text-sm sm:text-base font-medium leading-relaxed" style={{ color: landingConfig.accentColor }}>
            {landingConfig.description}
          </p>
        )}

        {(orderTiming || deliveryTiming) && (
          <div className="space-y-1.5">
            {orderTiming && (
              <div className="flex items-center justify-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 shrink-0 stroke-[2.2] text-gray-700" />
                <span className={`text-xs sm:text-sm font-semibold ${orderTiming.isOpen ? "text-emerald-700" : "text-red-600"}`}>
                  {orderTiming.isOpen ? "Open Now" : "Closed Now"} · Today: {orderTiming.todayLabel}
                </span>
              </div>
            )}
            {deliveryTiming && (
              <div className="flex items-center justify-center gap-1.5">
                <Bike className="h-3.5 w-3.5 shrink-0 stroke-[2.2] text-gray-700" />
                <span className={`text-xs sm:text-sm font-semibold ${deliveryTiming.isOpen ? "text-emerald-700" : "text-red-600"}`}>
                  Delivery {deliveryTiming.isOpen ? "Open Now" : "Closed Now"} · Today: {deliveryTiming.todayLabel}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3 pt-1">
          {/* Always present — the only way to actually reach the order flow
              from this page. */}
          <Link
            to="/order"
            className="block w-full rounded-full border-2 bg-white py-3.5 text-sm sm:text-base font-extrabold uppercase tracking-wide shadow-sm transition-transform active:scale-[0.98]"
            style={{ borderColor: landingConfig.accentColor, color: landingConfig.accentColor }}
          >
            Order Now
          </Link>

          {landingConfig.googleReviewUrl && (
            <a
              href={landingConfig.googleReviewUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2.5 w-full rounded-full border-2 bg-white py-3.5 text-sm sm:text-base font-extrabold uppercase tracking-wide shadow-sm transition-transform active:scale-[0.98]"
              style={{ borderColor: landingConfig.accentColor, color: landingConfig.accentColor }}
            >
              <GoogleIcon className="h-5 w-5 shrink-0" />
              Google Reviews
            </a>
          )}

          {businessPhone && (
            <a
              href={`tel:${businessPhone}`}
              className="flex items-center justify-center gap-2.5 w-full rounded-full border-2 bg-white py-3.5 text-sm sm:text-base font-extrabold uppercase tracking-wide shadow-sm transition-transform active:scale-[0.98]"
              style={{ borderColor: landingConfig.accentColor, color: landingConfig.accentColor }}
            >
              <Phone className="h-5 w-5 shrink-0" />
              {businessPhone}
            </a>
          )}
        </div>

        {SOCIAL_LINKS.length > 0 && (
          <div className="flex items-center justify-center gap-6 pt-2">
            {SOCIAL_LINKS.map(({ key, url, Icon, label }) => (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="transition-opacity hover:opacity-70"
                style={{ color: landingConfig.accentColor }}
              >
                <Icon className="w-6 h-6" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
